import { ACCOUNT_TYPES, type AccountType } from '@tshared/models/account'

type PublicEnvKey = 'API_BASE' | 'HEADER_HOST' | 'ACCOUNT_TYPE'

/**
 * Read a public env var in both Vite and Expo.
 * - Vite:  import.meta.env.VITE_*
 * - Expo:  process.env.EXPO_PUBLIC_*
 * - Fallback: process.env.VITE_* (Node / tests)
 */
function readPublicEnv(key: PublicEnvKey): string {
  const viteName = `VITE_${key}`
  const expoName = `EXPO_PUBLIC_${key}`

  const fromVite = (() => {
    try {
      const env = import.meta.env as Record<string, string | undefined> | undefined
      return env?.[viteName]
    } catch {
      // Non-Vite runtimes may not provide import.meta.env
      return undefined
    }
  })()

  const fromProcess =
    typeof process !== 'undefined' ? (process.env[expoName] ?? process.env[viteName]) : undefined

  // Prefer first non-empty so '' in one source falls through to another
  return fromVite || fromProcess || ''
}

function parseAccountType(value: string): AccountType | undefined {
  return (ACCOUNT_TYPES as readonly string[]).includes(value) ? (value as AccountType) : undefined
}

export class Config {
  private _apiBase!: string
  private _headerHost!: string
  private _accountType: AccountType = 'web'
  private _saveKv!: (key: string, value: string) => Promise<void>
  private _loadKv!: (key: string) => Promise<string>

  private _refreshIntervalMs?: number

  constructor() {
    this._apiBase = readPublicEnv('API_BASE')
    this._headerHost = readPublicEnv('HEADER_HOST')
    this._accountType = parseAccountType(readPublicEnv('ACCOUNT_TYPE')) ?? 'web'

    this._saveKv = async (key: string, value: string) => {
      localStorage.setItem(key, value)
    }
    this._loadKv = async (key: string) => {
      return localStorage.getItem(key) ?? JSON.stringify(null)
    }
  }

  getApiBase(): string {
    if (!this._apiBase) {
      throw new Error('apiBase is not set')
    }
    return this._apiBase
  }

  setApiBase(value: string): void {
    this._apiBase = value
  }

  getHeaderHost(): string {
    if (!this._headerHost) {
      throw new Error('headerHost is not set')
    }
    return this._headerHost
  }

  setHeaderHost(value: string): void {
    this._headerHost = value
  }

  getAccountType(): AccountType {
    return this._accountType
  }

  setAccountType(value: AccountType): void {
    this._accountType = value
  }

  getRefreshIntervalMs(): number {
    return this._refreshIntervalMs ?? 5 * 60 * 1000
  }

  setRefreshIntervalMs(value: number): void {
    this._refreshIntervalMs = value
  }

  getSaveKv(): (key: string, value: string) => Promise<void> {
    if (!this._saveKv) {
      throw new Error('saveKv is not set')
    }
    return this._saveKv
  }

  getLoadKv(): (key: string) => Promise<string> {
    if (!this._loadKv) {
      throw new Error('loadKv is not set')
    }
    return this._loadKv
  }

  setSaveKv(value: (key: string, value: string) => Promise<void>): void {
    this._saveKv = value
  }

  setLoadKv(value: (key: string) => Promise<string>): void {
    this._loadKv = value
  }
}

export const config = new Config()
